import {
  GatewayEntreprise,
  GatewayQuestionnaire,
  GatewayConformite,
} from "@conformite/gateway";
import { isInstanceComplete } from "@src/components/questionnaire/QuestionnaireForm/useIsQuestionnaireComplete";
import { useAssujettiListQuery } from "@src/data/assujetti/hook/useAssujettiListQuery";
import { QuestionnaireApi } from "@src/data/questionnaire/questionnaire.api";
import { questionnaireKeys } from "@src/data/questionnaire/hook/questionnaire.keys";
import { getRemediationStatus } from "@src/helper/remediation.helper";
import {
  axeConformiteToQuestionnaireSlug,
  getAvailableAxes,
} from "@src/helper/avancement.helper";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

type Params = {
  enabled: boolean;
  millesime?: number;
  siren?: string;
  entreprise?: GatewayEntreprise.EntrepriseAControllerResponseType;
};

type Result = {
  isComplete: boolean;
  isLoading: boolean;
  isEmpty: boolean;
  isRemediationRequired: boolean;
};

const CONTEXT_TYPE = "ASSUJETTI";

export type AssujettiAxeState = {
  isComplete: boolean;
  isRemediationRequired: boolean;
};

export function computeAssujettiAxeState(
  applicableSlugs: GatewayQuestionnaire.QuestionnaireSlug[],
  definitionBySlug: Map<
    string,
    GatewayQuestionnaire.QuestionnaireDefinitionResponse
  >,
  instancesByPersonne: GatewayQuestionnaire.QuestionnaireInstanceResponse[][]
): AssujettiAxeState {
  let isComplete = instancesByPersonne.length > 0 && applicableSlugs.length > 0;
  let isRemediationRequired = false;

  instancesByPersonne.forEach((personneInstances) => {
    applicableSlugs.forEach((slug) => {
      const definition = definitionBySlug.get(slug);
      if (!definition) {
        isComplete = false;
        return;
      }

      const slugInstances = personneInstances.filter(
        (i) => i.questionnaireDefinitionId === definition.id
      );
      if (slugInstances.length === 0) {
        isComplete = false;
        return;
      }

      const latest = slugInstances.reduce((acc, curr) =>
        acc.createdAt > curr.createdAt ? acc : curr
      );

      if (!isInstanceComplete(definition, latest)) {
        isComplete = false;
      }
      const status = getRemediationStatus(latest);
      if (status === "OUI" || status === "MANQUANT") {
        isRemediationRequired = true;
      }
    });
  });

  return { isComplete, isRemediationRequired };
}

export function useIsAssujettiAxeComplete({
  enabled,
  millesime,
  siren,
  entreprise,
}: Params): Result {
  const { data: assujettis, isLoading: isLoadingAssujettis } =
    useAssujettiListQuery(
      millesime ?? 0,
      siren ?? "",
      enabled && Boolean(millesime && siren)
    );

  const applicableSlugs = useMemo<
    GatewayQuestionnaire.QuestionnaireSlug[]
  >(() => {
    if (!enabled || !entreprise) return [];
    const axes = getAvailableAxes(entreprise);
    return axes
      .filter(
        (axe): axe is GatewayConformite.AxeConformiteAssujetti =>
          axe in axeConformiteToQuestionnaireSlug
      )
      .map((axe) => axeConformiteToQuestionnaireSlug[axe]);
  }, [enabled, entreprise]);

  const definitionQueries = useQueries({
    queries: applicableSlugs.map((slug) => ({
      queryKey: questionnaireKeys.definition(slug),
      queryFn: () => QuestionnaireApi.getDefinition(slug),
      staleTime: Infinity,
      enabled: applicableSlugs.length > 0,
    })),
  });

  const personneIds = useMemo(
    () => (assujettis ?? []).map((a) => a.id),
    [assujettis]
  );

  const instanceQueries = useQueries({
    queries: personneIds.map((personneId) => ({
      queryKey: questionnaireKeys.instances(CONTEXT_TYPE, personneId),
      queryFn: () => QuestionnaireApi.getInstances(CONTEXT_TYPE, personneId),
      staleTime: 30_000,
      enabled: enabled && personneIds.length > 0,
    })),
  });

  const isLoading =
    (enabled && Boolean(millesime && siren) && isLoadingAssujettis) ||
    definitionQueries.some((q) => q.isLoading) ||
    instanceQueries.some((q) => q.isLoading);

  const isEmpty =
    enabled && assujettis !== undefined && assujettis.length === 0;

  const { isComplete, isRemediationRequired } =
    useMemo<AssujettiAxeState>(() => {
      if (!enabled) return { isComplete: true, isRemediationRequired: false };
      if (
        !assujettis ||
        assujettis.length === 0 ||
        applicableSlugs.length === 0
      )
        return { isComplete: false, isRemediationRequired: false };

      const definitions = definitionQueries.map((q) => q.data);
      if (definitions.some((d) => !d))
        return { isComplete: false, isRemediationRequired: false };

      const definitionBySlug = new Map<
        string,
        GatewayQuestionnaire.QuestionnaireDefinitionResponse
      >();
      applicableSlugs.forEach((slug, idx) => {
        const def = definitions[idx];
        if (def) definitionBySlug.set(slug, def);
      });

      const instancesByPersonne = assujettis.map(
        (_, personneIdx) => instanceQueries[personneIdx]?.data ?? []
      );

      return computeAssujettiAxeState(
        applicableSlugs,
        definitionBySlug,
        instancesByPersonne
      );
    }, [
      enabled,
      assujettis,
      applicableSlugs,
      definitionQueries,
      instanceQueries,
    ]);

  return { isComplete, isLoading, isEmpty, isRemediationRequired };
}
